import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { InteractiveCampaignModal } from '../components/InteractiveCampaignModal';
import {
  interactiveCampaignApi,
  InteractiveCampaign,
  Connection,
} from '../services/interactiveCampaignApi';

export function InteractiveCampaignPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isBotOnly = location.pathname === '/campanhas/bot';
  const isCampaignOnly = location.pathname === '/campanhas/interativa';

  const [campaigns, setCampaigns] = useState<InteractiveCampaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<InteractiveCampaign[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);
  const [dropdownPlacement, setDropdownPlacement] = useState<{ top: number; left: number; bottom?: number; openUp: boolean } | null>(null);
  const actionsDropdownRef = useRef<HTMLDivElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadData();

    // Auto-refresh a cada 30 segundos
    const intervalId = setInterval(() => {
      loadData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsData, connectionsData] = await Promise.all([
        interactiveCampaignApi.getCampaigns(),
        interactiveCampaignApi.getConnections(),
      ]);
      setAllCampaigns(campaignsData);
      setCampaigns(campaignsData);
      setConnections(connectionsData);
    } catch (error: any) {
      toast.error(`Erro ao carregar dados: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Lista base: apenas BOT na página Fluxos/Bot, apenas Campanha Interativa na página Campanha Interativa
  const baseCampaigns = useMemo(() => {
    if (isBotOnly) return allCampaigns.filter(c => c.type === 'INBOUND');
    if (isCampaignOnly) return allCampaigns.filter(c => c.type === 'OUTBOUND');
    return allCampaigns;
  }, [allCampaigns, isBotOnly, isCampaignOnly]);

  // Filtrar campanhas baseado na busca e filtro de status
  useEffect(() => {
    let filtered = baseCampaigns;

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === statusFilter);
    }

    setCampaigns(filtered);
  }, [searchTerm, statusFilter, baseCampaigns]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!openActionsId) return;
      const inTrigger = actionsDropdownRef.current?.contains(target);
      const inMenu = actionsMenuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setOpenActionsId(null);
        setDropdownPlacement(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionsId]);

  const handleCreateCampaign = async (name: string, type: 'OUTBOUND' | 'INBOUND') => {
    try {
      const newCampaign = await interactiveCampaignApi.createCampaign({
        name,
        type,
        graph: {
          nodes: [],
          edges: [],
        },
      });

      const successMessage = type === 'INBOUND'
        ? 'Bot criado com sucesso! Configure o Início para definir palavras-chave e conexões.'
        : 'Campanha criada com sucesso! Configure o Início para definir conexões e categorias.';

      toast.success(successMessage);

      // Redirecionar para o builder: bot -> /campanhas/bot/:id, campanha -> /campanhas/interativa/:id
      navigate(isBotOnly ? `/campanhas/bot/${newCampaign.id}` : `/campanhas/interativa/${newCampaign.id}`);
    } catch (error: any) {
      toast.error(`Erro ao criar ${type === 'INBOUND' ? 'bot' : 'campanha'}: ${error.message}`);
    }
  };

  const handleEditCampaign = (id: string) => {
    navigate(isBotOnly ? `/campanhas/bot/${id}` : `/campanhas/interativa/${id}`);
  };

  const handleDeleteCampaign = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja excluir a campanha "${name}"?`)) {
      return;
    }

    try {
      setDeletingId(id);
      await interactiveCampaignApi.deleteCampaign(id);
      toast.success('Campanha excluída com sucesso!');
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao excluir campanha: ${error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePublishCampaign = async (id: string, name: string) => {
    if (!confirm(`Deseja publicar a campanha "${name}"? Ela começará a processar mensagens.`)) {
      return;
    }

    try {
      await interactiveCampaignApi.publishCampaign(id);
      toast.success('Campanha publicada!');
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao publicar campanha: ${error.message}`);
    }
  };

  const handlePauseCampaign = async (id: string, name: string) => {
    try {
      await interactiveCampaignApi.pauseCampaign(id);
      toast.success(`Campanha "${name}" pausada!`);
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao pausar campanha: ${error.message}`);
    }
  };

  const handleCompleteCampaign = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja finalizar a campanha "${name}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await interactiveCampaignApi.completeCampaign(id);
      toast.success(`Campanha "${name}" finalizada!`);
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao finalizar campanha: ${error.message}`);
    }
  };

  const handleDuplicateCampaign = async (id: string, name: string) => {
    try {
      const duplicatedCampaign = await interactiveCampaignApi.duplicateCampaign(id);
      toast.success(`Campanha "${name}" duplicada com sucesso!`);
      await loadData();

      // Redirecionar para editar a campanha duplicada
      navigate(isBotOnly ? `/campanhas/bot/${duplicatedCampaign.id}` : `/campanhas/interativa/${duplicatedCampaign.id}`);
    } catch (error: any) {
      toast.error(`Erro ao duplicar campanha: ${error.message}`);
    }
  };

  const handleToggleSelectCampaign = (campaignId: string) => {
    setSelectedCampaignIds(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSelectAllCampaigns = () => {
    if (selectedCampaignIds.length === campaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(campaigns.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCampaignIds.length === 0) return;

    if (!confirm(`Tem certeza que deseja excluir ${selectedCampaignIds.length} campanha(s) selecionada(s)?`)) {
      return;
    }

    try {
      setBulkDeleting(true);

      // Deletar todas as campanhas selecionadas
      await Promise.all(
        selectedCampaignIds.map(id => interactiveCampaignApi.deleteCampaign(id))
      );

      toast.success(`${selectedCampaignIds.length} campanha(s) excluída(s) com sucesso!`);
      setSelectedCampaignIds([]);
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao excluir campanhas: ${error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'STARTED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ✅ Iniciada
          </span>
        );
      case 'SCHEDULED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            📅 Aguardando
          </span>
        );
      case 'PAUSED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            ⏸️ Pausada
          </span>
        );
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-500 text-white">
            ✓ Finalizada
          </span>
        );
      default: // DRAFT
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            📝 Rascunho
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Extrai as conexões configuradas no nó Trigger
  const getTriggerConnections = (campaign: any) => {
    const graph = campaign.graph;
    if (!graph || !graph.nodes) return [];

    const triggerNode = graph.nodes.find((n: any) => n.data?.nodeType === 'trigger');
    if (!triggerNode || !triggerNode.data?.config) return [];

    return triggerNode.data.config.connections || [];
  };

  const pageTitle = isBotOnly ? 'Chatbot / Fluxos' : isCampaignOnly ? 'Campanha Interativa' : 'Automações';
  const emptyMessageNone = isBotOnly ? 'Nenhum bot criado' : isCampaignOnly ? 'Nenhuma campanha interativa criada' : 'Nenhuma automação criada';
  const emptyMessageFiltered = isBotOnly ? 'Nenhum bot encontrado' : isCampaignOnly ? 'Nenhuma campanha interativa encontrada' : 'Nenhuma automação encontrada';

  return (
    <div className="min-h-screen bg-ui-bg">
      <Header
        title={pageTitle}
        subtitle={
          selectedCampaignIds.length > 0
            ? `${selectedCampaignIds.length} ${isBotOnly ? 'bot(s)' : isCampaignOnly ? 'campanha(s) interativa(s)' : 'automação(ões)'} selecionada(s)`
            : isBotOnly
              ? `${baseCampaigns.length} bots`
              : isCampaignOnly
                ? `${baseCampaigns.length} campanhas interativas`
                : `${allCampaigns.length} automações cadastradas`
        }
        compactTopbar={isBotOnly || isCampaignOnly}
        actions={
          (isBotOnly || isCampaignOnly) ? undefined : (
          <div className="flex items-center gap-3">
            {selectedCampaignIds.length > 0 ? (
              <>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="btn-header-danger disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Excluir Selecionadas ({selectedCampaignIds.length})</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setSelectedCampaignIds([])}
                  className="btn-header-secondary"
                >
                  Cancelar Seleção
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-header-primary"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{isCampaignOnly ? 'Nova Campanha Interativa' : 'Nova Automação'}</span>
              </button>
            )}
          </div>
          )
        }
      />

      <PageLayout>
        <PageHeader
          title={pageTitle}
          breadcrumbs={
            isBotOnly
              ? [{ label: 'Home', href: '/' }, { label: 'Campanhas', href: '/campanhas' }, { label: 'Chatbot / Fluxos' }]
              : isCampaignOnly
                ? [{ label: 'Home', href: '/' }, { label: 'Campanhas', href: '/campanhas' }, { label: 'Campanha Interativa' }]
                : [{ label: 'Home', href: '/' }, { label: 'Automações' }]
          }
        />
        <div className="space-y-8">

        {/* Card de visão (padrão Nova Campanha) - Fluxos / Bot e Campanha Interativa */}
        {(isBotOnly || isCampaignOnly) && (
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {isBotOnly ? 'Visão dos bots' : 'Visão das campanhas interativas'}
              </h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => loadData()}
                  className="btn-atualizar"
                  title="Atualizar lista"
                >
                  <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                  Atualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  aria-label={isBotOnly ? 'Criar novo bot' : 'Criar nova campanha interativa'}
                >
                  <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                  {isBotOnly ? 'Novo Bot' : 'Nova Campanha Interativa'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className={isBotOnly ? 'fas fa-robot' : 'fas fa-bullhorn'} />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {isBotOnly ? 'Total de bots' : 'Total de campanhas'}
                  </p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{baseCampaigns.length}</h3>
              </div>
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-green-600 dark:text-green-400 text-xl" aria-hidden><i className="fas fa-circle-play" /></span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Ativas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{baseCampaigns.filter(c => c.status === 'STARTED').length}</h3>
              </div>
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden><i className="fas fa-circle-pause" /></span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Pausadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{baseCampaigns.filter(c => c.status === 'PAUSED').length}</h3>
              </div>
              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xl" aria-hidden><i className="fas fa-pen-to-square" /></span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Rascunho</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{baseCampaigns.filter(c => c.status === 'DRAFT').length}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Card único no padrão da página de campanhas */}
        <div className="card-padrao overflow-hidden">
          {/* Header: título à esquerda, busca e filtro à direita */}
          <div className={`px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4 ${(isBotOnly || isCampaignOnly) ? 'dark:border-gray-700' : ''}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className={`text-sm font-semibold whitespace-nowrap ${(isBotOnly || isCampaignOnly) ? 'text-gray-800 dark:text-white/90' : 'text-gray-700'}`}>
                {isBotOnly ? 'Bots' : isCampaignOnly ? 'Campanhas interativas' : 'Automações'}
              </h3>
              {campaigns.length > 0 && selectedCampaignIds.length === 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllCampaigns}
                  className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Selecionar todas ({campaigns.length})
                </button>
              )}
            </div>
            {selectedCampaignIds.length > 0 ? (
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setSelectedCampaignIds([])}
                  className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 rounded-lg transition-colors"
                >
                  Desmarcar todas
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {selectedCampaignIds.length} selecionada{selectedCampaignIds.length > 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="btn-header-warning"
                  aria-label="Excluir selecionadas"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Excluindo...
                    </>
                  ) : (
                    'Excluir Todas'
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                {(searchTerm || statusFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Limpar
                  </button>
                )}
                <div className="relative w-56">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm" aria-hidden />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">Todos</option>
                  <option value="DRAFT">Rascunho</option>
                  <option value="SCHEDULED">Agendada</option>
                  <option value="STARTED">Iniciada</option>
                  <option value="PAUSED">Pausada</option>
                  <option value="COMPLETED">Finalizada</option>
                </select>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
              <span className="ml-2 text-gray-600">Carregando...</span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400 font-medium">
                {(isBotOnly ? baseCampaigns.length === 0 : isCampaignOnly ? baseCampaigns.length === 0 : allCampaigns.length === 0) ? emptyMessageNone : emptyMessageFiltered}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedCampaignIds.length === campaigns.length && campaigns.length > 0}
                      onChange={handleSelectAllCampaigns}
                      className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  {!isBotOnly && !isCampaignOnly && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conexões
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criada em
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCampaignIds.includes(campaign.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleSelectCampaign(campaign.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white/90">
                          {campaign.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {campaign.graph?.nodes?.length || 0} nodes
                        </div>
                      </div>
                    </td>
                    {!isBotOnly && !isCampaignOnly && (
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                        {campaign.type === 'INBOUND' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            🤖 Bot
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            📣 Campanha Interativa
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                      {(() => {
                        const triggerConnectionIds = getTriggerConnections(campaign);
                        if (triggerConnectionIds.length === 0) {
                          return <div className="text-sm text-gray-500">-</div>;
                        }

                        const triggerConnections = connections.filter(c =>
                          triggerConnectionIds.includes(c.id)
                        );

                        if (triggerConnections.length === 0) {
                          return <div className="text-sm text-gray-500">-</div>;
                        }

                        return (
                          <div>
                            <div className="text-sm text-gray-900">
                              {triggerConnections[0].instanceName}
                            </div>
                            {triggerConnections.length > 1 && (
                              <div className="text-xs text-gray-500">
                                +{triggerConnections.length - 1} outra{triggerConnections.length > 2 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                      {getStatusBadge(campaign.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleEditCampaign(campaign.id); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                          title="Editar"
                        >
                          <i className="fas fa-pen text-sm shrink-0" aria-hidden />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(isBotOnly ? `/campanhas/bot/relatorio/${campaign.id}` : `/campanhas/interativa/relatorio/${campaign.id}`);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                          title="Ver Relatório"
                        >
                          <i className="fas fa-chart-line text-sm shrink-0" aria-hidden />
                          Relatório
                        </button>
                        {campaign.status === 'DRAFT' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePublishCampaign(campaign.id, campaign.name); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                            title="Ativar"
                          >
                            <i className="fas fa-play text-sm shrink-0" aria-hidden />
                            Ativar
                          </button>
                        )}
                        {campaign.status === 'STARTED' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePauseCampaign(campaign.id, campaign.name); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                            title="Pausar"
                          >
                            <i className="fas fa-pause text-sm shrink-0" aria-hidden />
                            Pausar
                          </button>
                        )}
                        {campaign.status === 'PAUSED' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePublishCampaign(campaign.id, campaign.name); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                            title="Ativar"
                          >
                            <i className="fas fa-play text-sm shrink-0" aria-hidden />
                            Ativar
                          </button>
                        )}
                        <div ref={openActionsId === campaign.id ? actionsDropdownRef : undefined}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openActionsId === campaign.id) {
                                setOpenActionsId(null);
                                setDropdownPlacement(null);
                              } else {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                const openUp = rect.bottom + 220 > window.innerHeight;
                                setDropdownPlacement({
                                  left: rect.right - 192,
                                  top: rect.bottom + 4,
                                  bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
                                  openUp,
                                });
                                setOpenActionsId(campaign.id);
                              }
                            }}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            title="Mais ações"
                            aria-expanded={openActionsId === campaign.id}
                          >
                            <i className="fas fa-ellipsis-v text-sm" aria-hidden />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
        )}
        </div>
        </div>
      </PageLayout>

      {/* Dropdown "Mais" renderizado em portal para não ser cortado pelo overflow */}
      {openActionsId && dropdownPlacement && (() => {
        const openCampaign = campaigns.find(c => c.id === openActionsId);
        if (!openCampaign) return null;
        const style: React.CSSProperties = {
          position: 'fixed',
          left: dropdownPlacement.left,
          width: 192,
          zIndex: 9999,
          ...(dropdownPlacement.openUp && dropdownPlacement.bottom !== undefined
            ? { bottom: dropdownPlacement.bottom, top: 'auto' }
            : { top: dropdownPlacement.top }),
        };
        return createPortal(
          <div
            ref={actionsMenuRef}
            className="py-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg"
            style={style}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpenActionsId(null); setDropdownPlacement(null); handleDuplicateCampaign(openCampaign.id, openCampaign.name); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-xl"
            >
              <i className="fas fa-copy w-4" aria-hidden />
              Duplicar
            </button>
            {(openCampaign.status === 'STARTED' || openCampaign.status === 'SCHEDULED' || openCampaign.status === 'PAUSED') && openCampaign.type === 'OUTBOUND' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenActionsId(null); setDropdownPlacement(null); handleCompleteCampaign(openCampaign.id, openCampaign.name); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <i className="fas fa-check-circle w-4" aria-hidden />
                Finalizar
              </button>
            )}
            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpenActionsId(null); setDropdownPlacement(null); handleDeleteCampaign(openCampaign.id, openCampaign.name); }}
              disabled={deletingId === openCampaign.id}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 rounded-b-xl"
            >
              {deletingId === openCampaign.id ? (
                <i className="fas fa-spinner fa-spin w-4" aria-hidden />
              ) : (
                <i className="fas fa-trash-alt w-4" aria-hidden />
              )}
              Excluir
            </button>
          </div>,
          document.body
        );
      })()}

      {/* Modal de Criar Campanha / Bot */}
      <InteractiveCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateCampaign}
        title={isBotOnly ? 'Novo Bot' : isCampaignOnly ? 'Nova Campanha Interativa' : 'Nova Automação'}
        forceType={isBotOnly ? 'INBOUND' : isCampaignOnly ? 'OUTBOUND' : undefined}
      />
    </div>
  );
}
